import React, {useEffect, useState} from "react";
import {Organization, Role, User, UserRole} from "../../types/users";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextinputField from "../CommonComponents/TextInputField/TextinputField";
import StyledAutoComplete from "../CommonComponents/StyledAutocomplete/StyledAutocomplete";
import {useTheme} from "../../contexts/theme";
import TransparentButton from "../CommonComponents/TransparentButton/TransparentButton";
import {Alert, Tooltip} from "@mui/material";
import RoleRendering from "../../guards/RoleRendering";

interface UserFormProps {
    user: User
    roles: Role[]
    organizations: Organization[]
    onSubmit: () => void
    sendFormLabel?: string,
    mode: "update" | "create" | "passwordChange",
    onInfoChange: (field: keyof User, value: string) => void;
    onRolesChange: (roles: Role[]) => void;
    onOrganizationsChange: (organization: Organization[]) => void;
}

const UserForm: React.FC<UserFormProps> = ({user, roles, organizations , onSubmit, sendFormLabel='', onInfoChange, onRolesChange, mode, onOrganizationsChange}) => {

    const theme = useTheme()

    const stylesProps = {
        borderBottomColor: theme.palette.orange,
        marginBottom: theme.spacing.md,
        backgroundColor: 'none',
        color: theme.palette.white,
        borderColor: theme.palette.white,
        borderRadius: theme.border.width.hard,
        size: 'medium' as 'medium' ,
        width: '100%',
    }

    const [passwordConfirm, setPasswordConfirm] = useState<string>('');
    const [buttonDisabled, setButtonDisabled] = useState<boolean>(true)

    const [orgMessage, setOrgMessage] = React.useState<string>("")

    function handleInputChange(e:any, field: keyof User) {
        onInfoChange(field, e.target.value)
    }

    function handleRoleChange(role: Role){
        onRolesChange([role])
    }

    function handleOrganizationChange(organization: Organization){

        if (user.organizations != undefined) {
            if (organization != user.organizations[0]) {
                setOrgMessage('Warning, you are overwriting the user\'s organization')
            }
        }

        onOrganizationsChange([organization])
    }

    function handleOnSubmit(e: any){
        e.preventDefault()
        onSubmit()
    }

    function handleChangePasswordConfirm(value : string){
        setPasswordConfirm(value)
    }

    useEffect(() => {
        if (mode === 'passwordChange') {
            if (passwordConfirm === user.password) setButtonDisabled(false)
            else
                setButtonDisabled(true)
        }else{
            setButtonDisabled(false)
        }
    }, [passwordConfirm, user.password]);

    return(
        <Box
        component="form"
        onSubmit={handleOnSubmit}
        >
            {orgMessage != "" &&
                (
                    <Alert variant="filled" severity="warning">
                        {orgMessage}
                    </Alert>
                )

            }

            <Grid container spacing={2} sx={{marginBottom: theme.spacing.md, marginTop:theme.spacing.xs}}>


                {(mode === 'update' || mode === 'create') && (

                    <><Grid item xs={12} md={6} lg={6}>
                        <TextinputField onChange={(e) => handleInputChange(e, 'email')} value={user.email}
                                        name={'email'} placeholder={'Email'} disabled={sendFormLabel.includes("Update")} type={'email'} variant={"outlined"}
                                        required={true} {...stylesProps} />
                    </Grid><Grid item xs={12} md={6} lg={6}>
                        <TextinputField onChange={(e) => handleInputChange(e, 'username')}
                                        disabled={sendFormLabel.includes("Update")} value={user.username}
                                        name={'username'} placeholder={'Username'} type={'text'}
                                        variant={"outlined"} {...stylesProps} />
                    </Grid><Grid item xs={12} md={6} lg={6}>
                        <TextinputField onChange={(e) => handleInputChange(e, 'name')} value={user.name} name={'name'}
                                        placeholder={'Name'} type={'text'} variant={"outlined"}
                                        required={true} {...stylesProps} />
                    </Grid><Grid item xs={12} md={6} lg={6}>
                        <TextinputField onChange={(e) => handleInputChange(e, 'surname')} value={user.surname}
                                        name={'surname'} placeholder={'Surname'} type={'text'} variant={"outlined"}
                                        required={true} {...stylesProps} />
                    </Grid></>
                ) }





                {
                    (mode === 'create' || mode === 'passwordChange')  &&
                    <>
                        <Grid item xs={12} md={6} lg={6}>
                            <TextinputField onChange={(e) => handleInputChange(e, 'password')} value={user.password}
                                            name={'password'} placeholder={'password'} type={'password'}
                                            variant={"outlined"} required={true} {...stylesProps} />
                        </Grid>

                        <Grid item xs={12} md={6} lg={6}>
                            <TextinputField onChange={(e) => handleChangePasswordConfirm(e.target.value)} value={passwordConfirm}
                                            name={'password confirm'} placeholder={'Confirm Password'} type={'password'}
                                            variant={"outlined"} required={true} {...stylesProps} />
                        </Grid>
                    </>



                }

                {(mode === 'update' || mode === 'create') &&

                    <><Grid item xs={12} md={6} lg={6}>
                        <StyledAutoComplete onChange={(_, newValue) => handleRoleChange(newValue)}
                                            value={user.roles[0] ? user.roles[0] : {id: "", name: ""}} label={'Roles'}
                                            name={'roles'} options={roles} labelField={'name'} valueField={'id'}
                                            required={true} multiple={false}/>
                    </Grid>
                        <Grid item xs={12} md={6} lg={6}>
                            <RoleRendering roles={[UserRole.SuperAdmin]}>
                        <StyledAutoComplete onChange={(_, newValue) => handleOrganizationChange(newValue)}
                                            value={user.organizations![0] ? user.organizations![0] : {id: "", name: ""}} label={'Organization'}
                                            name={'organizations'} options={organizations} labelField={'name'} valueField={'id'}
                                            required={true} multiple={false}/>
                            </RoleRendering>
                    </Grid></>
                }

            </Grid>
            <Box width={'100%'} display={'flex'} alignItems={'center'} justifyContent={'center'}>
                <Tooltip
                    title="Passwords must match to enable the submit button."
                    // You can use `disableInteractive` if you don't want the user to be able to click on the tooltip content
                    disableHoverListener={!buttonDisabled}
                >
                    <Box width={'150px'} height={'50px'}>
                        <TransparentButton disabled={buttonDisabled} width={'100%'} height={'100%'}   text={sendFormLabel} type={'submit'}/>
                    </Box>

                </Tooltip>

            </Box>
        </Box>)
}

export default UserForm